import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";
import { formatDateIt, romeToday, weekdayKey } from "@/lib/site";

const DAY_RANGE = /^\d{4}-\d{2}-\d{2}$/;

function mondayOf(ds) {
  const [y, m, d] = ds.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const dow = (date.getDay() + 6) % 7;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - dow);
}

export default function AdminWeeklyAgenda() {
  const { date: rawDate } = useParams();
  const anchor = DAY_RANGE.test(rawDate || "") ? rawDate : romeToday();
  const days = useMemo(() => {
    const mon = mondayOf(anchor);
    return Array.from({ length: 7 }, (_, i) =>
      new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + i).toLocaleDateString("en-CA"),
    );
  }, [anchor]);
  const [auth, setAuth] = useState("checking");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(() => setAuth(true)).catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth !== true) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const site = (await api.get("/site")).data;
        const appts = (await api.get("/admin/appointments", { params: { from_date: days[0], to_date: days[6] } })).data.appointments || [];
        const blocks = (await api.get("/admin/blocks")).data.blocks || [];
        if (cancelled) return;
        const settings = site.settings || {};
        const byDay = {};
        appts
          .filter((a) => a.status === "pending" || a.status === "confirmed")
          .sort((a, b) => (a.date === b.date ? a.start.localeCompare(b.start) : a.date.localeCompare(b.date)))
          .forEach((a) => {
            (byDay[a.date] = byDay[a.date] || []).push(a);
          });
        setData({
          contact: settings.contact || {},
          days: days.map((ds) => ({
            date: ds,
            windows: ((settings.hours || {})[weekdayKey(ds)]) || [],
            closed: (settings.blockedDates || []).includes(ds),
            blocks: blocks.filter((b) => b.date === ds),
            appts: byDay[ds] || [],
          })),
        });
      } catch (e) {
        if (!cancelled) setError(apiErrorDetail(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, days]);

  if (auth === "checking") return <div className="min-h-screen bg-stone-200" data-testid="weekly-agenda-checking" />;

  if (auth === false) {
    return (
      <div className="min-h-screen bg-stone-200 flex items-center justify-center px-6" data-testid="weekly-agenda-denied">
        <div className="bg-white rounded-3xl p-10 text-center shadow-soft max-w-sm">
          <img src="/images/logo-transparent.png" alt="K Nutrition" className="h-12 mx-auto mb-5" />
          <p className="font-display text-3xl mb-2">Agenda settimanale</p>
          <p className="text-sm text-charcoal/60 mb-7">Accedi al pannello per vedere e stampare l&apos;agenda della settimana.</p>
          <Link to="/admin" data-testid="weekly-agenda-goto-admin" className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors">
            <ArrowLeft size={14} /> Vai al pannello
          </Link>
        </div>
      </div>
    );
  }

  const fmtDay = (ds) =>
    new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "numeric", month: "long" }).format(new Date(ds + "T12:00:00"));

  return (
    <div className="min-h-screen bg-stone-200 py-8 px-4" data-testid="weekly-agenda-page">
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          .agenda-noprint { display: none !important; }
          .agenda-sheet { box-shadow: none !important; border-radius: 0 !important; max-width: none !important; width: auto !important; margin: 0 !important; padding: 0 !important; min-height: auto !important; }
          .week-day { break-inside: avoid; }
          body { background: #fff !important; }
        }
      `}</style>

      <div className="agenda-noprint max-w-[210mm] mx-auto flex items-center justify-between mb-5">
        <Link to="/admin" data-testid="weekly-agenda-back" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest2 font-medium text-charcoal/60 hover:text-deeptaupe transition-colors">
          <ArrowLeft size={14} /> Torna al pannello
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="weekly-agenda-print-button"
          className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors"
        >
          <Printer size={15} /> Stampa
        </button>
      </div>

      <div className="agenda-sheet bg-white rounded-2xl shadow-soft max-w-[210mm] mx-auto px-10 py-12" data-testid="weekly-agenda-sheet">
        <div className="flex items-start justify-between gap-6 pb-6 border-b-2 border-charcoal/80">
          <div>
            <img src="/images/logo-transparent.png" alt="K Nutrition" className="h-12 mb-3" />
            <p className="text-[11px] uppercase tracking-widest2 text-taupe">Dott.ssa Francesca Notarfrancesco · Biologa Nutrizionista</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-widest2 text-taupe mb-1">Agenda settimanale</p>
            <p className="font-display text-2xl capitalize" data-testid="weekly-agenda-period">
              {formatDateIt(days[0])} — {formatDateIt(days[6])}
            </p>
          </div>
        </div>

        {error && (
          <p className="my-8 rounded-xl bg-red-50 border border-red-200 text-red-700 px-5 py-3 text-sm" data-testid="weekly-agenda-error">
            {error}
          </p>
        )}

        {!data && !error ? (
          <p className="py-20 text-center text-charcoal/50 text-sm" data-testid="weekly-agenda-loading">Preparazione dell&apos;agenda…</p>
        ) : (
          <div className="mt-2">
            {data && data.days.map((d) => (
              <div key={d.date} className="week-day py-5 border-b border-stone-200 last:border-b-0" data-testid={`weekly-agenda-day-${d.date}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="font-display text-xl capitalize">{fmtDay(d.date)}</p>
                  <p className="text-[12px] text-charcoal/60">
                    {d.closed
                      ? "Studio chiuso"
                      : d.windows.length
                        ? d.windows.map((w) => `${w.start}–${w.end}`).join(" · ")
                        : "Non disponibile"}
                  </p>
                </div>
                {d.blocks.map((b) => (
                  <p key={b.mid} className="text-[12px] text-charcoal/60 mt-1" data-testid={`weekly-agenda-block-${b.mid}`}>
                    Bloccato: {b.start ? `${b.start}–${b.end}` : "giornata intera"}{b.reason ? ` (${b.reason})` : ""}
                  </p>
                ))}
                {d.appts.length === 0 ? (
                  <p className="text-[13px] text-charcoal/40 mt-2">Nessun appuntamento.</p>
                ) : (
                  d.appts.map((a) => (
                    <div key={a.mid} className="flex gap-5 py-2.5 break-inside-avoid" data-testid={`weekly-agenda-row-${a.mid}`}>
                      <span className="font-display text-lg text-deeptaupe w-28 shrink-0">{a.start}–{a.end}</span>
                      <span className="flex-1 min-w-0 text-[13px]">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-sm">{a.name}</span>
                          {a.status === "pending" && (
                            <span className="text-[10px] uppercase tracking-widest2 font-medium bg-peach text-deeptaupe rounded-full px-2.5 py-0.5">
                              da confermare
                            </span>
                          )}
                        </span>
                        <span className="block text-charcoal/70 mt-0.5">
                          {a.service_name} · {a.phone || "—"}{a.email ? ` · ${a.email}` : ""}
                        </span>
                      </span>
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 pt-4 border-t border-stone-200 flex flex-wrap justify-between gap-2 text-[11px] text-charcoal/50">
          <span>
            {data?.contact?.addressStreet || "—"}{data?.contact?.addressCity ? `, ${data.contact.addressCity}` : ""}{data?.contact?.phone ? ` · ${data.contact.phone}` : ""}
          </span>
          <span data-testid="weekly-agenda-printed-at">
            Stampata il {new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date())}
          </span>
        </div>
      </div>
    </div>
  );
}
